import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useDraggable } from '@dnd-kit/core';
import CategoryChip from './pm/CategoryChip';

function DraggableBacklogRow({ task, onTaskClick }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `backlog-${task.id}`,
    data: { task, fromBacklog: true },
  });

  const style = {
    opacity: isDragging ? 0.3 : 1,
    cursor: 'grab',
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      hover
      sx={{
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <TableCell sx={{ width: '40px', padding: '8px' }}>
        <IconButton
          size="small"
          {...attributes}
          {...listeners}
          sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell onClick={() => onTaskClick(task)} sx={{ cursor: 'pointer' }}>
        <Typography variant="body2" fontWeight={500}>
          {task.name}
        </Typography>
      </TableCell>
      <TableCell>
        {task.category_info ? (
          <CategoryChip category={task.category_info} sx={{ height: 20, fontSize: '0.7rem' }} />
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={task.priority}
          size="small"
          color={getPriorityColor(task.priority)}
        />
      </TableCell>
      <TableCell>
        {task.assignee ? (
          <Typography variant="body2">{task.assignee.username}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nicht zugewiesen
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {task.effort_estimate ? (
          <Typography variant="body2">{task.effort_estimate} Punkte</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {task.tags && task.tags.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {task.tags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                size="small"
                sx={{
                  backgroundColor: tag.color,
                  color: '#fff',
                  height: 20,
                  fontSize: '0.7rem',
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

function BacklogTable({ tasks, onTaskClick }) {
  const [selectedCategory, setSelectedCategory] = useState('all');

  if (!tasks || tasks.length === 0) {
    return null;
  }

  // Distinct categories present in the backlog (for the filter options)
  const presentCategories = [];
  const seen = new Set();
  let hasUncategorized = false;
  tasks.forEach((t) => {
    const c = t.category_info;
    if (c && c.key) {
      if (!seen.has(c.key)) { seen.add(c.key); presentCategories.push(c); }
    } else {
      hasUncategorized = true;
    }
  });

  const filteredTasks =
    selectedCategory === 'all'
      ? tasks
      : selectedCategory === '__none__'
        ? tasks.filter((t) => !t.category_info)
        : tasks.filter((t) => t.category_info && t.category_info.key === selectedCategory);

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Backlog ({filteredTasks.length}{selectedCategory !== 'all' ? ` / ${tasks.length}` : ''}) · zum Einplanen in eine Spalte ziehen
        </Typography>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Kategorie-Filter</InputLabel>
          <Select
            value={selectedCategory}
            label="Kategorie-Filter"
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <MenuItem value="all">Alle Kategorien</MenuItem>
            {presentCategories.map((c) => (
              <MenuItem key={c.key} value={c.key}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: c.color }} />
                  {c.label}
                </Box>
              </MenuItem>
            ))}
            {hasUncategorized && <MenuItem value="__none__">Ohne Kategorie</MenuItem>}
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell sx={{ width: '40px', padding: '8px' }}></TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Aufgabe</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Kategorie</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Priorität</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Zugewiesen</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Aufwand</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tags</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTasks.map((task) => (
              <DraggableBacklogRow
                key={task.id}
                task={task}
                onTaskClick={onTaskClick}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default BacklogTable;
